import { useEffect, useRef, useState } from 'react';
import { api, type AdDeckState, type AdPoster, type PlaybackState } from '../../lib/api';

function currentAdIndex(
  startedAtMs: number,
  intervalMs: number,
  count: number,
  nowMs: number,
): number {
  if (count <= 0) return 0;
  const raw = Math.floor((nowMs - startedAtMs) / Math.max(2000, intervalMs));
  return Math.min(Math.max(0, raw), count - 1);
}

export default function IdleScreen({
  playback,
  clockOffset,
}: {
  playback: PlaybackState | null;
  clockOffset: number;
}) {
  const [fallback, setFallback] = useState<AdDeckState | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now() + clockOffset);

  useEffect(() => {
    const id = window.setInterval(() => {
      setNowMs(Date.now() + clockOffset);
    }, 250);
    return () => window.clearInterval(id);
  }, [clockOffset]);

  useEffect(() => {
    if (playback?.ads?.items?.length) {
      setFallback(null);
      return;
    }
    let cancelled = false;
    void api
      .ads()
      .then((res) => {
        if (cancelled || !res.items.length) return;
        setFallback({
          items: res.items,
          started_at_ms: Date.now(),
          interval_ms: res.interval_ms || 12000,
          mode: playback?.phase === 'break' ? 'break' : 'idle',
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [playback?.ads, playback?.phase]);

  const ads = playback?.ads?.items?.length ? playback.ads : fallback;
  const posters: AdPoster[] = ads?.items ?? [];
  const index =
    ads && posters.length
      ? currentAdIndex(ads.started_at_ms, ads.interval_ms, posters.length, nowMs)
      : 0;
  const current = posters[index];
  const layersRef = useRef<Map<string, AdPoster>>(new Map());
  if (current) layersRef.current.set(current.url, current);
  const layers = [...layersRef.current.values()];

  if (!current) {
    return (
      <div className="tt-idle-screen">
        <div className="tt-idle-kicker">THEATER IS QUIET</div>
        <p className="tt-idle-copy">
          Pick a public domain movie or paste a YouTube URL to get the night started.
        </p>
      </div>
    );
  }

  return (
    <div className="tt-idle-screen has-ads">
      {layers.map((poster) => (
        <img
          key={poster.url}
          className={`tt-idle-ad${poster.url === current.url ? ' is-active' : ''}`}
          src={poster.url}
          alt=""
        />
      ))}
    </div>
  );
}
